import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import api from '../../api/endpoints';
import PageHeader from '../../components/PageHeader';
import { PartnerCard, ServiceCard, ProductCard } from '../../components/Cards';
import { openDirections } from '../../utils/openMap';
import {
  Button, Chip, Empty, Input, Loading, Modal,
} from '../../components/Ui';
import { vehicleFamilyOf } from '../../utils/vertical';
import { Reveal, stagger } from '../../components/Motion';
import { useApp } from '../../context/AppContext';
import { useCart } from '../../context/CartContext';

const RATINGS = [
  { value: '', label: 'Any rating' },
  { value: '3', label: '3★ & up' },
  { value: '4', label: '4★ & up' },
  { value: '4.5', label: '4.5★ & up' },
];
const DISTANCES = [
  { value: '2', label: 'Within 2 km' },
  { value: '5', label: '5 km' },
  { value: '10', label: '10 km' },
  { value: '25', label: '25 km' },
  { value: '', label: 'Anywhere' },
];

const SORTS = [
  { key: '', label: 'Recommended' },
  { key: 'rating', label: 'Top rated' },
  { key: 'price_low', label: 'Price: low to high' },
  { key: 'price_high', label: 'Price: high to low' },
  { key: 'newest', label: 'Newest' },
];

/**
 * Category listing. Shows services, products or shops depending on the category
 * type, so "Electrician" opens a service list and "Fruits" a product grid.
 */
const CategoryDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { location: place } = useApp();
  const { addItem, quantityOf } = useCart();

  const [category, setCategory] = useState((state && state.category) || null);
  const [tab, setTab] = useState(null);
  const [sort, setSort] = useState('');
  // Search within the category; rating and distance narrow the businesses.
  const [search, setSearch] = useState('');
  const [minRating, setMinRating] = useState('');
  const [withinKm, setWithinKm] = useState('25');
  // The sheet holds a draft until "Apply", so browsing choices never reloads the list.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [draft, setDraft] = useState({ sort: '', minRating: '', withinKm: '25' });
  const openFilters = () => { setDraft({ sort, minRating, withinKm }); setFiltersOpen(true); };
  const applyFilters = () => {
    setSort(draft.sort); setMinRating(draft.minRating); setWithinKm(draft.withinKm); setFiltersOpen(false);
  };
  const activeFilters = (minRating ? 1 : 0) + (withinKm !== '25' ? 1 : 0) + (sort ? 1 : 0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Arriving by a shared link there is no navigation state, so fetch the
  // category before the listing can decide which tab to open on.
  useEffect(() => {
    if (category) return;
    api.categories.detail(id)
      .then((res) => setCategory(res.data.category || res.data))
      .catch(() => setCategory(null));
  }, [id, category]);

  useEffect(() => {
    // Businesses first: a category tap should show who is out there, then what they sell.
    if (category && !tab) setTab(new URLSearchParams(window.location.search).get('tab') || 'shops');
  }, [category, tab]);

  const load = useCallback(async () => {
    if (!tab) return;
    setLoading(true);
    try {
      const params = {
        category: id, sort: sort || undefined, search: search || undefined, limit: 30,
      };
      let res;
      if (tab === 'products') res = await api.products.list(params);
      else if (tab === 'shops') {
        // Always around the place chosen in the header, never the GPS alone.
        res = await api.partners.list({
          category: id,
          lat: place.latitude,
          lng: place.longitude,
          radius: withinKm || 500,
          minRating: minRating || undefined,
          sort: sort || undefined,
          search: search || undefined,
        });
      } else res = await api.services.list(params);
      setItems(res.data || []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [id, tab, sort, search, minRating, withinKm, place.latitude, place.longitude]);

  useEffect(() => {
    // Typing waits a beat; every other change reloads at once.
    const timer = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);

  if (!category) return <Loading text="Loading category…" />;

  const isProduct = category.type === 'product';
  const TABS = isProduct
    ? [{ key: 'shops', label: 'Businesses' }, { key: 'products', label: 'Products' }]
    : [{ key: 'shops', label: 'Businesses' }, { key: 'services', label: 'Services' }];

  return (
    <div>
      <PageHeader
        title={`${category.icon || ''} ${category.name}`}
        subtitle={`${items.length} available near ${place.city}`}
      />

      {/* Bike / car service categories open the guided booking flow. */}
      {vehicleFamilyOf(category.name) ? (
        <button
          type="button"
          className="card press"
          onClick={() => navigate(vehicleFamilyOf(category.name) === 'car' ? '/bike-service?family=car' : '/bike-service')}
          style={{ width: '100%', textAlign: 'left', display: 'flex', gap: 12, alignItems: 'center', background: 'var(--primary)', color: '#fff', marginBottom: 16, border: 'none' }}
        >
          <span style={{ fontSize: 26 }}>{vehicleFamilyOf(category.name) === 'car' ? '🚗' : '🏍️'}</span>
          <span className="grow">
            <div className="t-title" style={{ color: '#fff' }}>{vehicleFamilyOf(category.name) === 'car' ? 'Book a car service' : 'Book a bike / scooter service'}</div>
            <div className="t-tiny" style={{ color: '#fff', opacity: 0.9 }}>Doorstep, pickup &amp; drop or at the garage - with an estimate before any work.</div>
          </span>
          <span style={{ fontWeight: 800 }}>›</span>
        </button>
      ) : null}

      <div className="card sh-sm" style={{ marginBottom: 16 }}>
        <div className="row" style={{ gap: 10, alignItems: 'center', marginBottom: 12 }}>
          <Input
            icon="🔍"
            placeholder={`Search in ${category.name}`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="grow"
            style={{ marginBottom: 0 }}
          />
          <button
            type="button"
            className={`filter-btn ${activeFilters ? 'on' : ''}`}
            onClick={openFilters}
            title="Filters"
            aria-label="Filters"
          >
            ⚙︎
            {activeFilters ? <span className="filter-count">{activeFilters}</span> : null}
          </button>
        </div>
        <div className="row wrap" style={{ gap: 8 }}>
          {TABS.map((t) => (
            <Chip key={t.key} label={t.label} active={tab === t.key} onClick={() => setTab(t.key)} />
          ))}
        </div>
      </div>

      {loading ? <Loading /> : !items.length ? (
        <Empty
          emoji={category.icon || '🔍'}
          title={`No ${tab === 'shops' ? 'businesses' : tab} yet in ${category.name}`}
          text="We are onboarding partners in your area. Check back soon."
        />
      ) : tab === 'products' ? (
        <div className="grid grid-products">
          {items.map((p, i) => (
            <Reveal key={p._id} delay={stagger(i, 35)}>
              <ProductCard
                product={p}
                inCart={quantityOf(p._id)}
                onAdd={() => addItem(p._id, 1).catch(() => {})}
                onClick={() => navigate(`/product/${p._id}`)}
              />
            </Reveal>
          ))}
        </div>
      ) : tab === 'shops' ? (
        <div className="grid grid-cards">
          {items.map((p, i) => (
            <Reveal key={p._id} delay={stagger(i, 40)}>
              <PartnerCard
                partner={p}
                onClick={() => navigate(`/business/${p._id}`)}
                onProducts={() => navigate(`/business/${p._id}?tab=products`)}
                onTour={() => navigate(`/business/${p._id}/tour`)}
                onCall={() => { window.location.href = `tel:${p.phone}`; }}
                onDirections={() => openDirections(p)}
              />
            </Reveal>
          ))}
        </div>
      ) : (
        <div className="grid grid-list">
          {items.map((s, i) => (
            <Reveal key={s._id} delay={stagger(i, 40)}>
              <ServiceCard service={s} onClick={() => navigate(`/service/${s._id}`)} />
            </Reveal>
          ))}
        </div>
      )}

      <Modal open={filtersOpen} onClose={() => setFiltersOpen(false)} title={`Filter ${category.name}`}>
        <label className="field-label">Sort by</label>
        <div className="row wrap" style={{ gap: 8, marginBottom: 14 }}>
          {SORTS.map((s) => (
            <Chip
              key={s.key || 'default'}
              label={tab === 'shops' && s.key === '' ? 'Nearest' : s.label}
              active={draft.sort === s.key}
              onClick={() => setDraft({ ...draft, sort: s.key })}
            />
          ))}
        </div>
        {tab === 'shops' ? (
          <>
            <label className="field-label">Rating</label>
            <div className="row wrap" style={{ gap: 8, marginBottom: 14 }}>
              {RATINGS.map((r) => (
                <Chip key={r.value || 'any'} label={r.label} active={draft.minRating === r.value} onClick={() => setDraft({ ...draft, minRating: r.value })} />
              ))}
            </div>
            <label className="field-label">Distance from {place.city}</label>
            <div className="row wrap" style={{ gap: 8, marginBottom: 14 }}>
              {DISTANCES.map((d) => (
                <Chip key={d.value || 'any'} label={d.label} active={draft.withinKm === d.value} onClick={() => setDraft({ ...draft, withinKm: d.value })} />
              ))}
            </div>
          </>
        ) : null}
        <div className="row" style={{ gap: 10 }}>
          <Button title="Apply" onClick={applyFilters} />
          <Button title="Reset" variant="light" onClick={() => setDraft({ sort: '', minRating: '', withinKm: '25' })} />
        </div>
      </Modal>
    </div>
  );
};

export default CategoryDetailPage;
